import {
  ArrowDown,
  ChevronSelectorVertical,
  Copy01,
  Edit01,
  HelpCircle,
  Trash01,
} from '@untitledui/icons';
import type {
  ComponentPropsWithRef,
  HTMLAttributes,
  ReactNode,
  Ref,
  TdHTMLAttributes,
  ThHTMLAttributes,
} from 'react';
import {
  createContext,
  isValidElement,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import type {
  CellProps as AriaCellProps,
  ColumnProps as AriaColumnProps,
  RowProps as AriaRowProps,
  TableHeaderProps as AriaTableHeaderProps,
  TableProps as AriaTableProps,
} from 'react-aria-components';
import {
  Cell as AriaCell,
  Collection as AriaCollection,
  Column as AriaColumn,
  ColumnResizer as AriaColumnResizer,
  Group as AriaGroup,
  ResizableTableContainer,
  Row as AriaRow,
  Table as AriaTable,
  TableBody as AriaTableBody,
  TableHeader as AriaTableHeader,
  useTableOptions,
} from 'react-aria-components';

import { Badge } from '@/lib/base/badges/badges';
import { Checkbox } from '@/lib/base/forms/checkbox';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import { cx } from '@/utils/cx';

export const TableRowActionsDropdown = () => (
  <Dropdown.Root>
    <Dropdown.DotsButton />

    <Dropdown.Popover className='w-min'>
      <Dropdown.Menu>
        <Dropdown.Item icon={Edit01}>
          <span className='pr-4'>Edit</span>
        </Dropdown.Item>
        <Dropdown.Item icon={Copy01}>
          <span className='pr-4'>Copy link</span>
        </Dropdown.Item>
        <Dropdown.Item icon={Trash01}>
          <span className='pr-4'>Delete</span>
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown.Popover>
  </Dropdown.Root>
);

interface TableContextValue {
  size: 'sm' | 'md';
  /**
   * When true, the enclosing `TableCard.Root` uses a flex-column layout so
   * the inner scroll area (`TableCard.ScrollArea`) can fill the remaining
   * height and scroll vertically. When false, the card sizes to its content
   * and only scrolls horizontally.
   */
  fillContainer: boolean;
}

const TableContext = createContext<TableContextValue | null>(null);

const useTableSize = (): 'sm' | 'md' => {
  const context = useContext(TableContext);
  return context ? context.size : 'md';
};

const useTableFillContainer = (): boolean => {
  const context = useContext(TableContext);
  return context ? context.fillContainer : false;
};

interface TableCardRootProps extends HTMLAttributes<HTMLDivElement> {
  size?: 'sm' | 'md';
  /**
   * When the card is placed in a bounded-height parent (e.g. a slideout or a
   * `flex-1 min-h-0` stack), set this to `true` so the card fills the
   * available height and the inner scroll area handles both vertical and
   * horizontal overflow. When the card is in normal document flow leave this
   * `false` — the card grows with content and only scrolls horizontally
   * while the page handles vertical scroll.
   */
  fillContainer?: boolean;
}

const TableCardRoot = ({
  children,
  className,
  size = 'md',
  fillContainer = false,
  ...props
}: TableCardRootProps) => {
  return (
    <TableContext.Provider value={{ size, fillContainer }}>
      <div
        {...props}
        className={cx(
          'overflow-hidden rounded-xl bg-primary shadow-xs ring-1 ring-secondary',
          fillContainer && 'flex flex-col flex-1 min-h-0',
          className
        )}
      >
        {children}
      </div>
    </TableContext.Provider>
  );
};

interface TableCardHeaderProps {
  /** The title of the table card header. */
  title: string;
  /** The badge displayed next to the title. */
  badge?: ReactNode;
  /** The description of the table card header. */
  description?: string;
  /** The content displayed after the title and badge. */
  contentTrailing?: ReactNode;
  /** The class name of the table card header. */
  className?: string;
}

const TableCardHeader = ({
  title,
  badge,
  description,
  contentTrailing,
  className,
}: TableCardHeaderProps) => {
  const size = useTableSize();
  const fillContainer = useTableFillContainer();

  return (
    <div
      className={cx(
        'relative flex flex-col items-start gap-4 border-b border-secondary bg-primary px-4 md:flex-row',
        size === 'sm' ? 'py-4 md:px-5' : 'py-5 md:px-6',
        // Keep the header pinned in a filling card so it doesn't shrink to
        // accommodate a tall scroll area.
        fillContainer && 'flex-shrink-0',
        className
      )}
    >
      <div className='flex flex-1 flex-col gap-0.5'>
        <div className='flex items-center gap-2'>
          <h2
            className={cx(
              'font-semibold text-primary',
              size === 'sm' ? 'text-md' : 'text-lg'
            )}
          >
            {title}
          </h2>
          {Boolean(badge) &&
            (isValidElement(badge) ? (
              badge
            ) : (
              <Badge color='brand' size='sm'>
                {badge}
              </Badge>
            ))}
        </div>
        {description && <p className='text-sm text-tertiary'>{description}</p>}
      </div>
      {contentTrailing}
    </div>
  );
};

interface TableRootProps
  extends
    AriaTableProps,
    Omit<ComponentPropsWithRef<'table'>, 'className' | 'slot' | 'style'> {
  size?: 'sm' | 'md';
}

const TableRoot = ({ className, size = 'md', ...props }: TableRootProps) => {
  const context = useContext(TableContext);

  return (
    <TableContext.Provider
      value={{
        size: context ? context.size : size,
        fillContainer: context ? context.fillContainer : false,
      }}
    >
      <AriaTable
        className={state =>
          cx(
            'w-full',
            typeof className === 'function' ? className(state) : className
          )
        }
        {...props}
      />
    </TableContext.Provider>
  );
};
TableRoot.displayName = 'Table';

interface TableHeaderProps<T extends object>
  extends
    AriaTableHeaderProps<T>,
    Omit<
      ComponentPropsWithRef<'thead'>,
      'children' | 'className' | 'slot' | 'style'
    > {
  bordered?: boolean;
}

const TableHeader = <T extends object>({
  columns,
  children,
  bordered = true,
  className,
  ...props
}: TableHeaderProps<T>) => {
  const size = useTableSize();
  const { selectionBehavior, selectionMode } = useTableOptions();

  return (
    <AriaTableHeader
      {...props}
      className={state =>
        cx(
          'relative bg-secondary sticky top-0 z-10',
          size === 'sm' ? 'h-9' : 'h-11',

          // Row border—using an "after" pseudo-element to avoid the border taking up space.
          bordered &&
            '[&>tr>th]:after:pointer-events-none [&>tr>th]:after:absolute [&>tr>th]:after:inset-x-0 [&>tr>th]:after:bottom-0 [&>tr>th]:after:h-px [&>tr>th]:after:bg-border-secondary [&>tr>th]:focus-visible:after:bg-transparent',

          typeof className === 'function' ? className(state) : className
        )
      }
    >
      {selectionBehavior === 'toggle' && (
        <AriaColumn
          width={size === 'sm' ? 36 : 44}
          minWidth={size === 'sm' ? 36 : 44}
          maxWidth={size === 'sm' ? 36 : 44}
          className={cx(
            'relative py-2 pr-0 pl-4',
            size === 'sm' ? 'w-9 md:pl-5' : 'w-11 md:pl-6'
          )}
        >
          {selectionMode === 'multiple' && (
            <div className='flex items-start'>
              <Checkbox slot='selection' size={size} />
            </div>
          )}
        </AriaColumn>
      )}
      <AriaCollection items={columns}>{children}</AriaCollection>
    </AriaTableHeader>
  );
};

TableHeader.displayName = 'TableHeader';

interface TableHeadProps
  extends
    AriaColumnProps,
    Omit<
      ThHTMLAttributes<HTMLTableCellElement>,
      'children' | 'className' | 'style' | 'id'
    > {
  label?: string;
  tooltip?: string;
  /** Renders a drag handle on the right edge for column resizing. Requires ResizableTableContainer. */
  resizable?: boolean;
  /**
   * Horizontal alignment of the header content. Defaults to `'left'`. Use
   * `'right'` for action/numeric columns whose cells are right-aligned so the
   * header lines up with its column's contents.
   */
  align?: 'left' | 'center' | 'right';
}

const TableHead = ({
  className,
  tooltip,
  label,
  children,
  resizable,
  align = 'left',
  ...props
}: TableHeadProps) => {
  const { selectionBehavior } = useTableOptions();

  return (
    <AriaColumn
      {...props}
      className={state =>
        cx(
          'relative p-0 px-6 py-2 outline-hidden focus-visible:z-1 focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-bg-primary focus-visible:ring-inset',
          selectionBehavior === 'toggle' && 'nth-2:pl-3',
          state.allowsSorting && 'cursor-pointer',
          // Show resize handle on column hover (arbitrary variant for real CSS specificity)
          resizable && '[&:hover_.col-resize-handle]:opacity-100',
          typeof className === 'function' ? className(state) : className
        )
      }
    >
      {state => (
        <>
          <AriaGroup
            className={cx(
              'flex items-center gap-1 overflow-hidden',
              align === 'right' && 'justify-end',
              align === 'center' && 'justify-center'
            )}
          >
            <div className='flex items-center gap-1 overflow-hidden'>
              {label && (
                <span className='text-xs font-semibold whitespace-nowrap text-quaternary'>
                  {label}
                </span>
              )}
              {typeof children === 'function' ? children(state) : children}
            </div>

            {tooltip && (
              <Tooltip title={tooltip} placement='top'>
                <TooltipTrigger className='cursor-pointer text-fg-quaternary transition duration-100 ease-linear hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover'>
                  <HelpCircle className='size-4' />
                </TooltipTrigger>
              </Tooltip>
            )}

            {state.allowsSorting &&
              (state.sortDirection ? (
                <ArrowDown
                  className={cx(
                    'size-3 stroke-[3px] text-fg-quaternary',
                    state.sortDirection === 'ascending' && 'rotate-180'
                  )}
                />
              ) : (
                <ChevronSelectorVertical
                  size={12}
                  strokeWidth={3}
                  className='text-fg-quaternary'
                />
              ))}
          </AriaGroup>
          {resizable && (
            <AriaColumnResizer
              className={resizerState =>
                cx(
                  'col-resize-handle',
                  'absolute top-0 right-[-3px] bottom-0 z-1 w-[7px] cursor-col-resize touch-none',
                  'border-r-2 border-dashed border-border-secondary transition-opacity duration-200',
                  // Focus-ring token, not raw brand: an active-manipulation
                  // highlight is the focus slot, and `brand-500` doesn't remap
                  // in dark mode (VISUAL_STYLE_GUIDE.md § Focus rings).
                  resizerState.isResizing
                    ? 'opacity-100 !border-focus-ring'
                    : 'opacity-0 hover:!opacity-100 hover:!border-focus-ring/60'
                )
              }
            />
          )}
        </>
      )}
    </AriaColumn>
  );
};
TableHead.displayName = 'TableHead';

interface TableRowProps<T extends object>
  extends
    AriaRowProps<T>,
    Omit<
      ComponentPropsWithRef<'tr'>,
      'children' | 'className' | 'onClick' | 'slot' | 'style' | 'id'
    > {
  highlightSelectedRow?: boolean;
}

const TableRow = <T extends object>({
  columns,
  children,
  className,
  highlightSelectedRow = true,
  ...props
}: TableRowProps<T>) => {
  const size = useTableSize();
  const { selectionBehavior } = useTableOptions();

  return (
    <AriaRow
      {...props}
      className={state =>
        cx(
          'relative outline-focus-ring transition-colors after:pointer-events-none hover:bg-secondary focus-visible:outline-2 focus-visible:-outline-offset-2',
          size === 'sm' ? 'h-14' : 'h-18',
          highlightSelectedRow && 'selected:bg-secondary',

          // Row border—using an "after" pseudo-element to avoid the border taking up space.
          '[&>td]:after:absolute [&>td]:after:inset-x-0 [&>td]:after:bottom-0 [&>td]:after:h-px [&>td]:after:w-full [&>td]:after:bg-border-secondary [&>td]:focus-visible:after:opacity-0 focus-visible:[&>td]:after:opacity-0',

          typeof className === 'function' ? className(state) : className
        )
      }
    >
      {selectionBehavior === 'toggle' && (
        <AriaCell
          className={cx(
            'relative py-2 pr-0 pl-4',
            size === 'sm' ? 'md:pl-5' : 'md:pl-6'
          )}
        >
          <div className='flex items-end'>
            <Checkbox slot='selection' size={size} />
          </div>
        </AriaCell>
      )}
      <AriaCollection items={columns}>{children}</AriaCollection>
    </AriaRow>
  );
};

TableRow.displayName = 'TableRow';

interface TableCellProps
  extends
    AriaCellProps,
    Omit<
      TdHTMLAttributes<HTMLTableCellElement>,
      'children' | 'className' | 'style' | 'id'
    > {
  ref?: Ref<HTMLTableCellElement>;
}

const TableCell = ({ className, children, ...props }: TableCellProps) => {
  const size = useTableSize();
  const { selectionBehavior } = useTableOptions();

  return (
    <AriaCell
      {...props}
      className={state =>
        cx(
          'relative text-sm text-tertiary overflow-hidden outline-focus-ring focus-visible:z-1 focus-visible:outline-2 focus-visible:-outline-offset-2',
          size === 'sm' && 'px-5 py-3',
          size === 'md' && 'px-6 py-4',

          selectionBehavior === 'toggle' && 'nth-2:pl-3',

          typeof className === 'function' ? className(state) : className
        )
      }
    >
      {children}
    </AriaCell>
  );
};
TableCell.displayName = 'TableCell';

interface TableExpandedRowProps {
  /** Stable id used by React Aria for row keying. */
  id: string | number;
  /**
   * Total number of columns in the table body (header column count). Used for
   * `colSpan` on the single inner cell so the expanded content spans the full
   * table width regardless of how many columns the table has.
   */
  colSpan: number;
  /** Extra classes for the row `<tr>`. */
  className?: string;
  /** Extra classes for the inner sticky content wrapper. */
  cellClassName?: string;
  children: ReactNode;
}

/**
 * Renders an "expanded detail" row that spans the full width of the table,
 * meant to be slotted in immediately after a data row to reveal extra content
 * (a form, a detail card, a timeline) without leaving the table body.
 *
 * Visual treatment:
 *   - Blends with the parent row above: the after-element bottom border on
 *     the data row is hidden so the two rows read as a single unit.
 *   - `!h-auto` so the row grows to fit the expanded content rather than
 *     staying pinned to the standard row height.
 *   - Secondary background so it visually recedes below the hovered row.
 *   - **Sticky on horizontal scroll**: the inner content wrapper is
 *     `position: sticky; left: 0` and sized to the scroll area's visible
 *     width (via the `--table-scroll-area-width` CSS variable set by
 *     `TableCard.ScrollArea`). When the user scrolls a wide table
 *     horizontally, the expanded detail stays pinned to the visible
 *     viewport rather than scrolling offscreen with the rest of the row.
 *
 * Usage:
 *   ```tsx
 *   <Table.ExpandedRow id={`${row.id}-expanded`} colSpan={totalColumns}>
 *     <DetailView ... />
 *   </Table.ExpandedRow>
 *   ```
 */
const TableExpandedRow = ({
  id,
  colSpan,
  className,
  cellClassName,
  children,
}: TableExpandedRowProps) => {
  // Smooth open animation that mirrors the shared <AccordionPanel>:
  // grid-template-rows interpolates from 0fr (collapsed) to 1fr (open).
  // The `<tr>` itself is conditionally rendered by the parent (so this
  // component mounts when the row is expanded), but we want the height
  // to grow from 0 to natural content height rather than popping
  // open. Same trick as <AccordionPanel>:
  //
  //   1. Start with grid-rows-[0fr] on mount.
  //   2. Flip to grid-rows-[1fr] one frame later via requestAnimationFrame
  //      so the browser has a stable 0fr paint to interpolate from.
  //
  // We can't animate the close — by the time React unmounts this row,
  // the DOM is already gone. Symmetric collapse would require keeping
  // the row mounted past the toggle, which the parent table doesn't
  // currently do. Open-only animation is still a meaningful improvement
  // over the instant pop.
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    const handle = requestAnimationFrame(() => setIsOpen(true));
    return () => cancelAnimationFrame(handle);
  }, []);

  // Use AriaRow directly instead of TableRow to avoid the checkbox cell that
  // TableRow injects for selectable tables. The extra cell breaks the colSpan
  // calculation, so the expanded row wouldn't span the full table width.
  return (
    <AriaRow
      id={id}
      className={cx('!h-auto bg-secondary [&>td]:after:hidden', className)}
    >
      <TableCell colSpan={colSpan} className='!p-0'>
        <div
          className={cx(
            // `sticky left-0` pins the content to the left of the scroll
            // container so horizontal scroll doesn't take the detail pane
            // offscreen. The width falls back to `100%` when the CSS
            // variable isn't present (e.g. the expanded row is used outside
            // a `TableCard.ScrollArea`).
            'sticky left-0 w-[var(--table-scroll-area-width,100%)]',
            // Pure-CSS expand via grid-template-rows interpolation
            // (0fr → 1fr). Matches the shared <AccordionPanel>
            // animation timing exactly.
            'grid grid-rows-[0fr] transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none',
            isOpen && 'grid-rows-[1fr]'
          )}
        >
          <div
            className={cx('min-h-0 overflow-hidden', cellClassName ?? 'p-5')}
          >
            {children}
          </div>
        </div>
      </TableCell>
    </AriaRow>
  );
};
TableExpandedRow.displayName = 'TableExpandedRow';

interface TableCardScrollAreaProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/**
 * Tracks a scrollable element's width and scroll position and exposes both:
 *
 *   - `edges` — whether there's hidden content to the left / right so the
 *     caller can render a gradient "there's more to scroll" hint.
 *
 *   - A CSS custom property `--table-scroll-area-width` set on the element
 *     itself, kept in sync with `clientWidth`. `Table.ExpandedRow`'s
 *     sticky inner wrapper reads this via `w-[var(--table-scroll-area-width,100%)]`
 *     so the expanded detail always sizes to the scroll area's *visible*
 *     width, even when the table's intrinsic width is much greater.
 *     (Using a measured CSS variable is more reliable than container
 *     queries inside `<td>` elements, which behave inconsistently across
 *     browsers due to table layout rules.)
 *
 * Listens to `scroll` on the element itself and a `ResizeObserver` for the
 * element plus the content it wraps — either dimension changing (window
 * resize, row added, columns widened) flips state back in sync.
 */
function useScrollEdgeShadows(elementRef: React.RefObject<HTMLElement | null>) {
  const [edges, setEdges] = useState({ left: false, right: false });

  useEffect(() => {
    const el = elementRef.current;
    if (!el) {
      return;
    }
    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      // Publish the visible inline width so sticky descendants (e.g. the
      // expanded-row detail pane) can size to it regardless of how wide the
      // underlying table is.
      el.style.setProperty('--table-scroll-area-width', `${clientWidth}px`);
      // 1px tolerance swallows sub-pixel rounding errors that would leave
      // the fade lingering at the extremes even when fully scrolled.
      const left = scrollLeft > 1;
      const right = scrollLeft + clientWidth < scrollWidth - 1;
      setEdges(prev =>
        prev.left === left && prev.right === right ? prev : { left, right }
      );
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(el);
    // Also observe the first child (the <table>) — its intrinsic width
    // changes when data loads and we want state to update immediately.
    if (el.firstElementChild) {
      resizeObserver.observe(el.firstElementChild);
    }
    return () => {
      el.removeEventListener('scroll', update);
      resizeObserver.disconnect();
    };
  }, [elementRef]);

  return edges;
}

/**
 * Scroll container for a `<Table>`. Works both inside a `TableCard.Root`
 * (where it picks up the card's `fillContainer` mode) and standalone (where
 * it provides horizontal scroll + edge fades for any table that might
 * overflow its parent's width).
 *
 * Handles three responsibilities so callers don't have to:
 *
 * 1. **Overflow** — horizontal scroll always; vertical scroll also when the
 *    parent card is in `fillContainer` mode (the card has a bounded height
 *    and the table needs to scroll internally rather than overflowing the
 *    page). Outside `fillContainer` mode the page handles vertical scroll.
 *
 * 2. **Container-query context** — declares `container-type: inline-size`
 *    so `Table.ExpandedRow`'s inner sticky wrapper can size to `100cqi`
 *    (the scroll area's visible inline width). Without this, expanded rows
 *    would stretch to the intrinsic table width and overflow horizontally.
 *
 * 3. **Edge scroll hints** — when the table is wider than the viewport, a
 *    soft gradient fades in on whichever side(s) have hidden content, so
 *    the user knows they can scroll to reveal more columns.
 *
 * Standalone usage example:
 *   <TableCard.ScrollArea>
 *     <Table>...</Table>
 *   </TableCard.ScrollArea>
 */
const TableCardScrollArea = ({
  className,
  children,
  style,
  ...props
}: TableCardScrollAreaProps) => {
  const fillContainer = useTableFillContainer();
  const scrollRef = useRef<HTMLDivElement>(null);
  const edges = useScrollEdgeShadows(scrollRef);

  return (
    // `relative` anchors the absolute-positioned fade overlays below. The
    // flex treatment mirrors what the scroll child used to carry so filling
    // tables still behave the same.
    <div
      className={cx(
        'relative',
        fillContainer && 'flex flex-1 min-h-0 flex-col'
      )}
    >
      <div
        {...props}
        ref={scrollRef}
        style={style}
        className={cx(
          fillContainer ? 'flex-1 min-h-0 overflow-auto' : 'overflow-x-auto',
          className
        )}
      >
        {children}
      </div>
      {/* Edge fade hints. `pointer-events-none` keeps them from swallowing
          scroll/click. `transition-opacity` softens the appear/disappear so
          the fade doesn't pop on during a scroll. */}
      <div
        aria-hidden
        className={cx(
          'pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-primary to-transparent transition-opacity duration-150',
          edges.left ? 'opacity-100' : 'opacity-0'
        )}
      />
      <div
        aria-hidden
        className={cx(
          'pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-primary to-transparent transition-opacity duration-150',
          edges.right ? 'opacity-100' : 'opacity-0'
        )}
      />
    </div>
  );
};
TableCardScrollArea.displayName = 'TableCardScrollArea';

interface TableCardFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/**
 * Sticky footer slot for pagination or bulk-action bars. Sits below the
 * scroll area inside a `TableCard.Root` and receives the card's bottom
 * border treatment automatically.
 */
const TableCardFooter = ({
  className,
  children,
  ...props
}: TableCardFooterProps) => {
  const fillContainer = useTableFillContainer();
  return (
    <div
      {...props}
      className={cx(
        'border-t border-secondary px-4 py-3 md:px-6 md:pt-3 md:pb-4',
        // Keep the footer pinned when the card is filling so it doesn't
        // shrink alongside the scroll area.
        fillContainer && 'flex-shrink-0',
        className
      )}
    >
      {children}
    </div>
  );
};
TableCardFooter.displayName = 'TableCardFooter';

const TableCard = {
  Root: TableCardRoot,
  Header: TableCardHeader,
  ScrollArea: TableCardScrollArea,
  Footer: TableCardFooter,
};

const Table = TableRoot as typeof TableRoot & {
  Body: typeof AriaTableBody;
  Cell: typeof TableCell;
  ExpandedRow: typeof TableExpandedRow;
  Head: typeof TableHead;
  Header: typeof TableHeader;
  Row: typeof TableRow;
};
Table.Body = AriaTableBody;
Table.Cell = TableCell;
Table.ExpandedRow = TableExpandedRow;
Table.Head = TableHead;
Table.Header = TableHeader;
Table.Row = TableRow;

export { ResizableTableContainer, Table, TableCard };
